import React, { useMemo, useState } from 'react';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import styles from './ProductivityPerContributorTile.module.scss';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import Benchmark from '../../../../../common/Benchmark/Benchmark';
import Tooltip from '../../../../../common/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../../common/Icon/Icon';
import { useAppSelector } from '../../../../../../redux/hooks';
import GraphCard from '../../../../../common/GraphCard/GraphCard';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import isEmpty from 'lodash/isEmpty';
import { useGetAGCAISummaryQuery } from '../../../../../../redux/services/agcAIAPI';
import {
  CurrencyFormatter,
  CurrencyInputFormatter,
} from '../../../../../../utils/currencyFormatter';
import { NumberFormatter } from '../../../../../../utils/NumberFormatter';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
  affiliateCode?: string;
}

const KPI_CONFIG = {
  productivityPerBuyer: {
    titleKey: 'kpiTitles.buyerProductivity',
    labelKey: 'kpiLabels.buyerCount',
    descriptionKey: 'kpiDescription.productivityPerBuyer',
    metricKey: 'downline_buyer_count',
    dropdownElement: 'buyerCount',
  },
  productivityPerContributor: {
    titleKey: 'kpiTitles.productivityPerContributor',
    labelKey: 'kpiLabels.contributorCount',
    descriptionKey: 'kpiDescription.productivityPerContributor',
    metricKey: 'downline_contributor_count',
    dropdownElement: 'contributorCount',
  },
} as const;

type KpiCodeType = keyof typeof KPI_CONFIG;

export const ProductivityPerContributorTile: React.FC<SummaryProp> = ({
  summary,
  loading = false,
  affiliateCode = '010',
}) => {
  const { t } = useTranslation();
  const benchmark = summary?.benchmark;
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

  const kpiCode = (summary?.kpiCode as KpiCodeType) ?? 'productivityPerContributor';
  const config = KPI_CONFIG[kpiCode] ?? KPI_CONFIG.productivityPerContributor;

  const demandSales = summary?.metricSourceData.find(
    (v: { key: string }) => v.key === 'product_demand_sales'
  );
  const demandSalesValue = demandSales?.value ?? null;
  const demandSalesCurrency = demandSales?.currency;
  const contributorCount = summary?.metricSourceData.find(
    (v: { key: string }) => v.key === 'downline_contributor_count'
  );
  const contributorCountValue = contributorCount?.value ?? null;
  const contributorSalesValue = summary?.kpiValue ?? null;
  const contributorSalesCurrency = summary?.kpiCurrency;

  return (
    <KPITile
      title={t('kpiTitles.productivityPerContributor')}
      dataTestId="productivity-per-contributor-tile"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.prodDemandSales')}</div>
            <div className={styles.value_content}>
              {demandSalesValue !== null && demandSalesValue !== undefined
                ? CurrencyInputFormatter(demandSalesCurrency, demandSalesValue, true)
                : '--'}
            </div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>/</div>
            <div className={styles.value_content}></div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.contributorCount')}</div>
            <div className={styles.value_content}>
              {summary?.metricSourceData?.[1]?.value
                ? NumberFormatter(affiliateCode, contributorCountValue, true)
                : '--'}
            </div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>=</div>
            <div className={styles.value_content}></div>
          </div>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.dollarContributor')}</div>
            <div className={`${styles.value_content} ${styles.value_highlight}`}>
              {contributorSalesValue !== null && contributorSalesValue !== undefined
                ? CurrencyInputFormatter(contributorSalesCurrency, contributorSalesValue, true)
                : '--'}
            </div>
          </div>
        </div>

        <div className={styles.bar_wrapper}>
          {
            <Benchmark
              minValue={benchmark?.min}
              maxValue={benchmark?.max}
              averageValue={benchmark?.marketAverage}
              medianValue={benchmark?.median}
              currentBenchmarkValue={summary?.kpiValue}
              quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
              benchmarkDataType={summary?.benchmarkDataType}
              currency={summary?.kpiCurrency}
              isPercentValue={false}
              truncateDecimals
            />
          }
          <div className={styles.bar_footer}>
            {t('market_average')}{' '}
            <span className={styles.bar_footer_value}>
              {String(CurrencyFormatter(summary?.kpiCurrency, benchmark?.marketAverage)).replace(
                /\.00$/,
                ''
              )}
            </span>
            <span>
              <Tooltip
                content={<span>{t('kpiTooltips.productivityPerContributor')}</span>}
                width="250"
                height="auto"
                bottom="30px"
                left="685%"
              >
                <span>
                  <Icon
                    name="info_icon"
                    size={16}
                    color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                  />
                </span>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </KPITile>
  );
};

export const ProductivityPerContributorDetail: React.FC<{
  onClose?: () => void;
}> = ({ onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('productivity');

  const { data: kpiDetailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    {
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: 'productivityPerContributor' }] } as any,
    },
    { skip: !onClose }
  );

  const { data: agcAISummary, isLoading: isLoadingAiSummary } = useGetAGCAISummaryQuery(
    {
      affiliateCode,
      aboNumber,
    },
    {
      skip: Number(affiliateCode) === 0 || aboNumber === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const detailData = kpiDetailsResponse?.body?.kpis?.[0] || {};
  const detailView = detailData?.detail || {};

  // Extract chart data from productivityView and countView
  const productivityViewCharts = detailView?.productivityView?.chart || [];
  const countViewCharts = detailView?.countView?.chart || [];

  // Extract line chart data from productivityView
  const productivityLineChart = productivityViewCharts.find(
    (chart: any) => chart.chartType === 'lineChart'
  );
  const productivityLineChartData = productivityLineChart?.chartDetail?.lineChartDetail;

  // Extract line chart data from countView
  const countLineChart = countViewCharts.find(
    (chart: any) => chart.chartType === 'lineChart'
  );
  const countLineChartData = countLineChart?.chartDetail?.lineChartDetail;

  const productivityTableChart = productivityViewCharts.find(
    (chart: any) => chart.chartType === 'table'
  );
  const productivityTableData = productivityTableChart?.chartDetail?.tableDetail;

  const countTableChart = countViewCharts.find(
    (chart: any) => chart.chartType === 'table'
  );
  const countTableData = countTableChart?.chartDetail?.tableDetail;

  const activeTableData =
    selectedPeriod === 'productivity' ? productivityTableData : countTableData;

  const activeLineChartData =
    selectedPeriod === 'productivity' ? productivityLineChartData : countLineChartData;

  const activeChartSummary =
    selectedPeriod === 'productivity'
      ? productivityLineChart?.chartSummary
      : countLineChart?.chartSummary;

  const periodItems = useMemo(
    () => [
      { code: 'productivity', label: t('kpiLabels.productivityPerContributor') },
      { code: 'contributorCount', label: t('kpiLabels.contributorCount') },
    ],
    [t]
  );

  if (isLoading) return <Skeleton height="400px" />;

  const aiContent =
    agcAISummary?.body?.summary?.length > 300
      ? `${agcAISummary?.body?.summary?.slice(0, 300)}...`
      : agcAISummary?.body?.summary;
  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>
          {periodItems.find((item) => item.code === selectedPeriod)?.label}
        </div>
        <div className={styles.desc}>{t('kpiDescription.productivityPerContributor')}</div>
      </div>
      <ViewModeSwitcher
        mode={viewMode}
        onChangeMode={setViewMode}
        disabled={false}
        selectedTimeRangeCode={selectedPeriod}
        onSelectTimeRange={setSelectedPeriod}
        timeRanges={periodItems}
      />
    </div>
  );

  const leftContent = (
    <div className={styles.left_content}>
      {viewMode === 'left' ? (
        isEmpty(activeLineChartData) ? (
          <div className={styles.no_data}>{t('noDataAvailable')}</div>
        ) : (
          <GraphCard
            lineChartData={activeLineChartData}
            currency={detailData?.kpiCurrency || 'USD'}
            chartSummaryData={activeChartSummary}
            truncateDecimals
          />
        )
      ) : isEmpty(activeTableData) ? (
        <div className={styles.no_data}>{t('noDataAvailable')}</div>
      ) : (
        <GenericTable
          tableData={activeTableData}
          kpiCode="productivityPerContributor"
          currency={detailData?.kpiCurrency || 'USD'}
          isOnlyNegativeHighlight
          highlightRows={[0]}
          truncateDecimals
        />
      )}
    </div>
  );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={24}
        titleSize={14}
      />
    )
  ) : null;

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="productivity-per-contributor-detail"
    />
  );
};

export default ProductivityPerContributorTile;
